import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTable, CellProps, Column, Cell, HeaderGroup, Row } from 'react-table'
import Box from '@mui/material/Box'
import Chip, { ChipProps } from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ChatRoundedIcon from '@mui/icons-material/ChatRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import PersonAddAlt1 from '@mui/icons-material/PersonAddAlt1'
import HowToRegIcon from '@mui/icons-material/HowToReg'
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt'

import DelayedRender from '@/components/hoc/DelayedRender'
import ChipButton from '@/components/ui/buttons/ChipButton'

import colors from '@/assets/scss/_themes-vars.module.scss'

export type StaffData = {
  id: string
  name: string
  surname: string
  gender: 'M' | 'F' | 'I'
  city?: string
  cps?: string
  email?: string
  pluginId?: string
  contactStatus?: 'notRequested' | 'handle' | 'pending' | 'accepted' | 'refused'
}

export type HandleContactAction = 'request' | 'accept' | 'refuse' | 'cancel'

export type StaffsTableProps = {
  /**
   * If `true`, the first row is selected by default
   */
  autoHightlight?: boolean
  data: StaffData[]
  loading?: boolean
  showAction?: boolean
  onSelect?: (data: StaffData) => void
  onHandleContact?: (contactId: string, action: HandleContactAction) => void
}

const rowHeight = 60

export function StaffsTable(props: StaffsTableProps) {
  const { autoHightlight = true, showAction = true, data, loading, onSelect, onHandleContact } = props
  const [selectedRow, setSelectedRow] = useState<string>()
  const { t } = useTranslation()

  const columns = useMemo(() => {
    return [
      {
        Header: t('page.staffs.staffsTable.header.surname'),
        accessor: 'surname',
        Cell: ({ value }: CellProps<StaffData, string>) => (
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '15px' }}>
            {value.toUpperCase()}
          </Typography>
        ),
      },
      {
        Header: t('page.staffs.staffsTable.header.name'),
        accessor: 'name',
        Cell: ({ value }: CellProps<StaffData, string>) => (
          <Typography variant="body2" sx={{ color: colors.gray600, fontSize: '15px' }}>
            {value}
          </Typography>
        ),
      },
      {
        Header: t('page.staffs.staffsTable.header.gender'),
        accessor: 'gender',
        Cell: ({ value: gender }: CellProps<StaffData, StaffData['gender']>) => {
          if (!gender) return null
          let color: ChipProps['color']
          switch (gender) {
            case 'M':
              color = 'primary'
              break
            case 'F':
              color = 'secondary'
              break
            default:
              color = 'info'
              break
          }
          return <Chip color={color} label={gender} size="small" variant="filled" />
        },
      },
      {
        Header: t('page.staffs.staffsTable.header.city'),
        accessor: 'city',
      },
      {
        Header: t('page.staffs.staffsTable.header.cps'),
        accessor: 'cps',
      },
      {
        Header: t('page.staffs.staffsTable.header.email'),
        accessor: 'email',
      },
    ] as Column<StaffData>[]
  }, [t])

  const { getTableBodyProps, headerGroups, prepareRow, rows } = useTable<StaffData>({
    columns,
    data,
  })

  useEffect(() => {
    if (autoHightlight && selectedRow === undefined && !!rows.length) {
      setSelectedRow(rows[0].original.id)
    }
  }, [autoHightlight, rows, selectedRow])

  const handleSelect = (row: Row<StaffData>) => {
    setSelectedRow(row.original.id)
    onSelect?.(row.original)
  }

  // const [chatOpen, setChatOpen] = useState<boolean>(false)

  // const selectRoom = (state: RootState): any => state.staff.createDirectMsg.room
  // var room_data = useAppSelector(selectRoom)
  // const dispatch = useAppDispatch()

  // const authUser = useAppSelector(selectAuthUser)
  // const { enqueueSnackbar } = useSnackbar()

  // const toggleMessenger = (email: string) => {
  //   setChatOpen((prevState) => !prevState)
  //   dispatch(createDirectMsg(email))

  //   if (authUser?.displayChat === false) {
  //     openRocketchat()
  //   }
  // }

  // const openRocketchat = async () => {
  //   if (room_data && room_data.room)
  //     window.open(import.meta.env.VITE_ROCKETCHAT_URL + '/direct/' + room_data.room._id.toString(), 'newwindow1', 'width=500,height=350')

  //   if ((room_data && !room_data.room) || !room_data) enqueueSnackbar(t('notification.userNotFound.error'), { variant: 'error' })
  // }

  return (
    <Table stickyHeader sx={{ minWidth: 650 }}>
      <TableHead sx={{ bgcolor: colors.gray100 }}>
        {headerGroups.map((headerGroup: HeaderGroup<StaffData>) => (
          <TableRow {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column: HeaderGroup<StaffData>) => {
              return (
                <TableCell {...column.getHeaderProps()} scope="col">
                  {column.render('Header')}
                </TableCell>
              )
            })}
            {showAction && <TableCell align="right">{t('common:table.header.action')}</TableCell>}
          </TableRow>
        ))}
      </TableHead>
      <TableBody {...getTableBodyProps()} sx={{ position: 'relative' }}>
        {loading && (
          <DelayedRender delay={300}>
            <TableRow>
              <TableCell sx={{ border: 'none', padding: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'rgba(255, 255, 255, 0.7)',
                  }}
                >
                  <CircularProgress size={20} />
                </Box>
              </TableCell>
            </TableRow>
          </DelayedRender>
        )}
        {rows.map((row) => {
          prepareRow(row)
          return (
            <TableRow
              {...row.getRowProps()}
              onClick={() => handleSelect(row)}
              selected={selectedRow === row.original.id}
              // sx={{ cursor: 'pointer' }}
            >
              {row.cells.map((cell: Cell<StaffData>) => (
                <TableCell {...cell.getCellProps()} align="left" sx={{ height: rowHeight + 'px', verticalAlign: 'middle' }}>
                  {cell.render('Cell')}
                </TableCell>
              ))}
              {showAction && (
                <TableCell align="right">
                  <Stack justifyContent="flex-end" direction="row" spacing={1}>
                    {row.original.contactStatus === 'notRequested' && (
                      <Tooltip title={<span>{t('page.staffs.sendContactRequestButton.tooltip')}</span>}>
                        <span>
                          <ChipButton color="info" onClick={() => onHandleContact?.(row.original.id, 'request')} data-testid="add-to-contact-button">
                            <PersonAddAlt1 />
                          </ChipButton>
                        </span>
                      </Tooltip>
                    )}
                    {row.original.contactStatus === 'handle' && (
                      <>
                        <Tooltip title={<span>{t('page.staffs.refuseContactRequestButton.tooltip')}</span>}>
                          <span>
                            <ChipButton color="error" onClick={() => onHandleContact?.(row.original.id, 'refuse')}>
                              <ClearRoundedIcon />
                            </ChipButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={<span>{t('page.staffs.acceptContactRequestButton.tooltip')}</span>}>
                          <span>
                            <ChipButton color="success" onClick={() => onHandleContact?.(row.original.id, 'accept')}>
                              <CheckRoundedIcon />
                            </ChipButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                    {row.original.contactStatus === 'accepted' && (
                      <Tooltip title={<span>{t('page.staffs.cancelContactRequestButton.tooltip')}</span>}>
                        <span>
                          <ChipButton color="success" onClick={() => onHandleContact?.(row.original.id, 'cancel')}>
                            <HowToRegIcon />
                          </ChipButton>
                        </span>
                      </Tooltip>
                    )}
                    {row.original.contactStatus === 'pending' && (
                      <Tooltip title={<span>{t('page.staffs.cancelContactRequestButton.tooltip')}</span>}>
                        <span>
                          <ChipButton color="secondary" onClick={() => onHandleContact?.(row.original.id, 'cancel')}>
                            <PersonAddAltIcon />
                          </ChipButton>
                        </span>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              )}
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

export default StaffsTable
